import { useQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ForbiddenError } from '../api/errors';
import { inventoryApi } from '../api/inventory-api-instance';
import { notificationsApi } from '../api/notifications-api';
import { ErrorState } from '../components/ErrorState';
import { HeaderHint } from '../components/HeaderHint';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import { statusLabels, statusesForFamily } from '../domain/status';
import {
  computeAssignedVsLocation,
  computeExceptionQueue,
  computeFamilyCounts,
  computeInventorySummary,
  computePartTypeBreakdown,
  computeStatusBreakdown,
  FULL_FLEET_ITEMS_QUERY_KEY,
  FULL_FLEET_PAGE_SIZE,
} from '../features/dashboard/aggregations';
import {
  activeRowLabels,
  activeRows,
  computeActiveBreakdown,
  HEARTBEAT_TYPES,
} from '../features/dashboard/active-breakdown';
import {
  cameraAllocationColumns,
  computeCustomerAllocation,
  computeCustomerCameraAllocation,
  computeRevisionBreakdown,
  computeSystemSummary,
  deviceCategoryForPart,
  deviceCategories,
  type CustomerAllocation,
  type DeviceCategory,
} from '../features/dashboard/operational-summaries';
import { allocationCsv, allocationCsvFilename } from '../features/dashboard/allocation-csv';
import { operationallyVisibleItems } from '../features/inventory/operational-visibility';
import { uniquePartTypes } from '../features/inventory/part-type-filter';
import { toMap } from '../lib/collections';
import { downloadCsv } from '../lib/csv';
import { humanizeIdentifier } from '../lib/formatting';

const IN_TRANSIT_STATUSES = 'SHIPPED_TO_WAREHOUSE,SHIPPED_TO_CUSTOMER,RETURN_SHIPPED_TO_WAREHOUSE';
const WAREHOUSE_STATUSES = 'DELIVERED_TO_WAREHOUSE,IN_WAREHOUSE_STORAGE';

type GenerationDeviceCategory = 'gen1-camera' | 'gen2-camera' | 'gen1-winch' | 'gen2-winch';

const generationSegmentsByPartType: Partial<
  Record<
    string,
    readonly {
      key: GenerationDeviceCategory;
      label: 'Gen1' | 'Gen2';
      cssVar: '--generation-gen1' | '--generation-gen2';
    }[]
  >
> = {
  CAMERA_SYSTEM: [
    { key: 'gen1-camera', label: 'Gen1', cssVar: '--generation-gen1' },
    { key: 'gen2-camera', label: 'Gen2', cssVar: '--generation-gen2' },
  ],
  WINCH_SYSTEM: [
    { key: 'gen1-winch', label: 'Gen1', cssVar: '--generation-gen1' },
    { key: 'gen2-winch', label: 'Gen2', cssVar: '--generation-gen2' },
  ],
};

function isGenerationDeviceCategory(
  category: DeviceCategory | null,
): category is GenerationDeviceCategory {
  return (
    category === 'gen1-camera' ||
    category === 'gen2-camera' ||
    category === 'gen1-winch' ||
    category === 'gen2-winch'
  );
}

// Carries the dashboard's active part-type scope into an outbound /inventory
// link, so the number a user just saw on a tile/bar matches what they land on
// — same convention as InventoryPage's own partType filter.
function withPartType(href: string, partType: string): string {
  if (!partType) return href;
  const [path, query = ''] = href.split('?');
  const params = new URLSearchParams(query);
  params.set('partType', partType);
  return `${path}?${params.toString()}`;
}

interface AllocationColumn {
  key: string;
  label: string;
  title: string;
  /** Shown in place of the counts: "Coming soon", or why there are none yet. */
  placeholder?: string;
}

const deviceColumns: readonly AllocationColumn[] = deviceCategories.map((category) => ({
  key: category.key,
  label: category.shortLabel,
  title: category.label,
}));

// The camera columns that come from the month's heartbeats.
const penActivityColumns = new Set(['activePens', 'notReporting']);

function cameraColumnsFor(penActivityPlaceholder: string | undefined): AllocationColumn[] {
  return cameraAllocationColumns.map((column) => {
    if (column.key === 'contracted') return { ...column, placeholder: 'Coming soon' };
    if (penActivityPlaceholder && penActivityColumns.has(column.key)) {
      return { ...column, placeholder: penActivityPlaceholder };
    }
    return column;
  });
}

/** The current month and the eleven before it, as YYYY-MM, newest first (UTC). */
function recentMonths(now: Date = new Date()): string[] {
  return Array.from({ length: 12 }, (_, index) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1))
      .toISOString()
      .slice(0, 7),
  );
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function AllocationCell({
  column,
  counts,
  showZero = false,
}: {
  column: AllocationColumn;
  counts: Readonly<Record<string, number>>;
  showZero?: boolean;
}) {
  if (column.placeholder) {
    return (
      <td className="allocation-coming-soon" title={column.title}>
        {column.placeholder}
      </td>
    );
  }
  const count = counts[column.key] ?? 0;
  return <td>{count || showZero ? count.toLocaleString() : '—'}</td>;
}

export function DashboardPage() {
  const [summaryView, setSummaryView] = useState<'allocation' | 'system'>('system');
  // The allocation table exposes the same geography at every depth:
  // customer, country, region, then site. Expansion is keyed by that tree so
  // operators can reveal only the part of the fleet they need to inspect.
  const [expandedAllocationRows, setExpandedAllocationRows] = useState<Set<string>>(new Set());
  const [params, setParams] = useSearchParams();
  const partType = params.get('partType') ?? '';
  const allocationCustomerId = params.get('allocationCustomer') ?? '';
  const allocationView = params.get('allocationView') === 'cameras' ? 'cameras' : 'devices';
  const months = useMemo(() => recentMonths(), []);
  const requestedMonth = params.get('allocationMonth') ?? '';
  const allocationMonth = months.includes(requestedMonth) ? requestedMonth : months[0];
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });

  const sitesById = useMemo(() => toMap(sites.data), [sites.data]);
  const customersById = useMemo(() => toMap(customers.data), [customers.data]);
  const partsById = useMemo(() => toMap(parts.data), [parts.data]);
  const partTypes = useMemo(() => uniquePartTypes(parts.data ?? []), [parts.data]);
  const allItems = useMemo(
    () => operationallyVisibleItems(items.data?.data ?? [], sitesById),
    [items.data, sitesById],
  );
  // Scopes every stat below to the selected part type. The "Inventory by part
  // type" panel deliberately stays unscoped (computed from allItems) — it's
  // the breakdown across types, so filtering it to one type would just be a
  // single, redundant bar.
  const scopedItems = useMemo(() => {
    if (!partType) return allItems;
    return allItems.filter((item) => partsById.get(item.partId)?.type === partType);
  }, [allItems, partsById, partType]);
  const summary = useMemo(() => computeInventorySummary(scopedItems), [scopedItems]);
  // Heartbeats, to split Active into in service / gone quiet
  // (tidaltech/koi#8447). The live API returns every cached row in one call.
  const usages = useQuery({
    queryKey: ['item-usages', 'all'],
    queryFn: () => inventoryApi.getItemUsages(allItems.map((item) => item.id)),
    enabled: allItems.length > 0,
  });
  const siteCodeById = useMemo(
    () => new Map((sites.data ?? []).map((site) => [site.id, site.name])),
    [sites.data],
  );
  const activeBreakdown = useMemo(
    () => computeActiveBreakdown(scopedItems, partsById, usages.data ?? {}, siteCodeById),
    [scopedItems, partsById, usages.data, siteCodeById],
  );

  const familySegments = useMemo(() => computeFamilyCounts(scopedItems), [scopedItems]);
  const statusBreakdown = useMemo(() => computeStatusBreakdown(scopedItems, 6), [scopedItems]);
  const exceptionQueue = useMemo(
    () => computeExceptionQueue(scopedItems, sitesById, 5),
    [scopedItems, sitesById],
  );
  const assignedVsLocation = useMemo(
    () => computeAssignedVsLocation(scopedItems, sitesById, customersById, 6),
    [scopedItems, sitesById, customersById],
  );
  const partTypeBreakdown = useMemo(
    () => computePartTypeBreakdown(allItems, partsById, 8),
    [allItems, partsById],
  );
  const partTypeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of allItems) {
      const type = partsById.get(item.partId)?.type;
      if (type) counts.set(type, (counts.get(type) ?? 0) + 1);
    }
    return counts;
  }, [allItems, partsById]);
  const deviceAllocation = useMemo(
    () => computeCustomerAllocation(scopedItems, partsById, sitesById, customersById),
    [scopedItems, partsById, sitesById, customersById],
  );
  // Which pens cameras reported from in the month, for Active pens
  // (tidaltech/koi#8447). Staff only, and only read for the camera view.
  const penActivity = useQuery({
    queryKey: ['pen-activity', allocationMonth],
    queryFn: () => notificationsApi.listPenActivity(allocationMonth),
    enabled: allocationView === 'cameras',
    retry: false,
  });
  const cameraAllocation = useMemo(
    () =>
      computeCustomerCameraAllocation(
        scopedItems,
        partsById,
        sitesById,
        customersById,
        usages.data ?? {},
        penActivity.data?.activity ?? [],
      ),
    [scopedItems, partsById, sitesById, customersById, usages.data, penActivity.data],
  );
  const penActivityPlaceholder = penActivity.isError
    ? 'Unavailable'
    : penActivity.isPending
      ? 'Loading…'
      : undefined;
  const penActivityNote = penActivity.isError
    ? penActivity.error instanceof ForbiddenError
      ? 'Active pens are for staff only'
      : "Active pens couldn't load; try again later"
    : penActivity.data?.throughDay
      ? `Active pens through ${dayLabel(penActivity.data.throughDay)}`
      : penActivity.data
        ? 'No heartbeats this month yet'
        : 'Loading active pens…';
  const cameraColumns = useMemo(
    () => cameraColumnsFor(penActivityPlaceholder),
    [penActivityPlaceholder],
  );
  // Both views share one table: the device view counts per generation and
  // device type, the camera view (tidaltech/koi#8447) Gen1 and Gen2 cameras
  // combined, by status and heartbeat.
  const customerAllocation: CustomerAllocation<Record<string, number>> =
    allocationView === 'cameras' ? cameraAllocation : deviceAllocation;
  const allocationColumns: readonly AllocationColumn[] =
    allocationView === 'cameras' ? cameraColumns : deviceColumns;
  const systemSummary = useMemo(
    () => computeSystemSummary(scopedItems, partsById),
    [scopedItems, partsById],
  );
  // Hardware versions within Gen2 (G2.1, G2.2, ...; tidaltech/koi#8153).
  // Scoped like the other summaries, so a part-type filter hides the
  // categories it excludes.
  const revisionBreakdowns = useMemo(
    () =>
      (['gen2-camera', 'gen2-winch'] as const)
        .map((category) => computeRevisionBreakdown(scopedItems, partsById, category))
        .filter((breakdown) => breakdown !== null),
    [scopedItems, partsById],
  );
  const displayedCustomerAllocation = useMemo(
    () =>
      allocationCustomerId
        ? customerAllocation.rows.filter((row) => row.customerId === allocationCustomerId)
        : customerAllocation.rows,
    [allocationCustomerId, customerAllocation.rows],
  );
  const displayedAllocationTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const row of displayedCustomerAllocation) {
      for (const [key, count] of Object.entries(row.counts)) {
        totals[key] = (totals[key] ?? 0) + count;
      }
    }
    return totals;
  }, [displayedCustomerAllocation]);
  const displayedAllocationTotal = useMemo(
    () => displayedCustomerAllocation.reduce((total, row) => total + row.total, 0),
    [displayedCustomerAllocation],
  );
  const generationDeviceCounts = useMemo(() => {
    const counts: Record<GenerationDeviceCategory, number> = {
      'gen1-camera': 0,
      'gen2-camera': 0,
      'gen1-winch': 0,
      'gen2-winch': 0,
    };
    for (const item of allItems) {
      const category = deviceCategoryForPart(partsById.get(item.partId));
      if (isGenerationDeviceCategory(category)) counts[category] += 1;
    }
    return counts;
  }, [allItems, partsById]);

  function updatePartType(value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set('partType', value);
    else next.delete('partType');
    setParams(next);
  }

  function updateAllocationCustomer(value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set('allocationCustomer', value);
    else next.delete('allocationCustomer');
    setParams(next);
  }

  function updateAllocationMonth(value: string): void {
    const next = new URLSearchParams(params);
    if (value && value !== months[0]) next.set('allocationMonth', value);
    else next.delete('allocationMonth');
    setParams(next);
  }

  // Downloads the allocation table as shown: the view, customer filter and,
  // for cameras, the month (tidaltech/koi#8469).
  function exportAllocationCsv(): void {
    const options =
      allocationView === 'cameras'
        ? { month: allocationMonth, throughDay: penActivity.data?.throughDay }
        : {};
    downloadCsv(
      allocationCsvFilename(allocationView, options),
      allocationCsv(displayedCustomerAllocation, allocationColumns, options),
    );
  }

  function updateAllocationView(value: 'devices' | 'cameras'): void {
    const next = new URLSearchParams(params);
    if (value === 'cameras') next.set('allocationView', value);
    else next.delete('allocationView');
    setParams(next);
  }

  function toggleAllocationRow(key: string): void {
    setExpandedAllocationRows((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function allocationSiteHref(customerId: string, siteIds: readonly string[]): string {
    return withPartType(
      `/inventory?customerId=${encodeURIComponent(customerId)}&siteIds=${siteIds.map(encodeURIComponent).join(',')}`,
      partType,
    );
  }

  if (items.isLoading) return <PageLoadingState label="Loading operations overview…" />;
  if (items.isError || !items.data) {
    return (
      <ErrorState
        title="Dashboard could not be loaded"
        rpcName="ListItems"
        onRetry={() => void items.refetch()}
      />
    );
  }

  const fleetTotal = familySegments.reduce((sum, segment) => sum + segment.count, 0) || 1;
  const largestStatusCount = Math.max(1, ...statusBreakdown.map((row) => row.count));
  const largestPartCount = Math.max(1, ...partTypeBreakdown.map((row) => row.count));
  const refreshedAt = new Date(
    Math.max(
      items.dataUpdatedAt,
      sites.dataUpdatedAt,
      customers.dataUpdatedAt,
      parts.dataUpdatedAt,
    ) || Date.now(),
  );

  const metrics = [
    ['Needs attention', summary.needsAttention, '/inventory?attention=true', 'danger'],
    ['Service & repair', summary.service, '/inventory?family=service', 'danger'],
    [
      'Status missing',
      familySegments.find((segment) => segment.family === 'data-quality')?.count ?? 0,
      '/inventory?status=STATUS_UNSPECIFIED',
      'warning',
    ],
    ['In transit', summary.inTransit, `/inventory?statuses=${IN_TRANSIT_STATUSES}`, undefined],
    ['Warehouse', summary.warehouse, `/inventory?statuses=${WAREHOUSE_STATUSES}`, undefined],
    [
      'In service',
      usages.data ? activeBreakdown.rows.inService.total : summary.active,
      '/inventory?status=ACTIVE',
      undefined,
    ],
  ] as const;
  const summaryScope =
    summaryView === 'system'
      ? `${systemSummary.total.toLocaleString()} mapped${
          systemSummary.omitted > 0
            ? ` · ${systemSummary.omitted.toLocaleString()} procurement or transit`
            : ''
        }`
      : `${displayedCustomerAllocation.length.toLocaleString()} customer${
          displayedCustomerAllocation.length === 1 ? '' : 's'
        } · ${displayedAllocationTotal.toLocaleString()} mapped`;

  return (
    <>
      <PageHeader
        title="Operations overview"
        titleAction={
          <label className="dashboard-part-filter">
            <span className="filter-field-label">Part type</span>
            <SearchableSelect
              ariaLabel="Part type"
              onChange={updatePartType}
              options={[
                { value: '', label: 'All part types', count: allItems.length, countLabel: 'items' },
                ...partTypes.map((value) => ({
                  value,
                  label: humanizeIdentifier(value),
                  aliases: [value],
                  count: partTypeCounts.get(value) ?? 0,
                  countLabel: 'items',
                })),
              ]}
              placeholder="All part types"
              value={partType}
            />
          </label>
        }
        description={
          partType
            ? `${summary.total.toLocaleString()} ${humanizeIdentifier(partType)} items across the fleet`
            : `${summary.total.toLocaleString()} serialized items across the fleet`
        }
        actions={
          <p className="last-updated">
            Updated {refreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        }
      />

      <section className="metric-row metric-row-priority" aria-label="Operational priorities">
        {metrics.map(([label, value, href, variant]) => (
          <Link
            key={label}
            to={withPartType(href, partType)}
            className={`metric-tile${variant ? ` metric-tile-${variant}` : ''}`}
          >
            <span className="metric-label">{label}</span>
            <strong className="metric-value">{value.toLocaleString()}</strong>
          </Link>
        ))}
      </section>

      <section className="panel" aria-label="Active devices">
        <div className="panel-heading">
          <h2>Active devices</h2>
          <span className="quiet-link">
            {usages.isLoading ? 'Loading heartbeats…' : `${summary.active.toLocaleString()} Active`}
          </span>
        </div>
        <div className="summary-table active-breakdown-table">
          <table>
            <thead>
              <tr>
                <th scope="col">By heartbeat</th>
                <th scope="col">Total</th>
                {HEARTBEAT_TYPES.map((type) => (
                  <th scope="col" key={type}>
                    {humanizeIdentifier(type)}
                  </th>
                ))}
                <th scope="col">Other</th>
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row) => {
                const counts = activeBreakdown.rows[row];
                return (
                  <tr key={row}>
                    <th scope="row">
                      {row === 'replaced' ? (
                        <Link to={withPartType('/inventory?status=REPLACED', partType)}>
                          {activeRowLabels[row]}
                        </Link>
                      ) : (
                        activeRowLabels[row]
                      )}
                    </th>
                    <td>{counts.total.toLocaleString()}</td>
                    {HEARTBEAT_TYPES.map((type) => (
                      <td key={type}>{counts[type].toLocaleString()}</td>
                    ))}
                    <td>{counts.other.toLocaleString()}</td>
                  </tr>
                );
              })}
              <tr className="active-breakdown-note">
                <th scope="row">Experimental: dual pens, counted above</th>
                <td>{activeBreakdown.experimental.total.toLocaleString()}</td>
                {HEARTBEAT_TYPES.map((type) => (
                  <td key={type}>{activeBreakdown.experimental[type].toLocaleString()}</td>
                ))}
                <td>{activeBreakdown.experimental.other.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div className="dashboard-priority-grid">
        <section className="panel dashboard-exception-panel">
          <div className="panel-heading">
            <h2>Exception queue</h2>
            <Link to={withPartType('/inventory?attention=true', partType)} className="quiet-link">
              View all
            </Link>
          </div>
          {exceptionQueue.length === 0 ? (
            <p className="empty-note">No items currently need attention.</p>
          ) : (
            <ul className="exception-queue">
              {exceptionQueue.map((row) => (
                <li key={row.itemId}>
                  <Link
                    to={`/inventory/${encodeURIComponent(row.itemId)}`}
                    className="exception-row"
                    aria-label={`Open ${row.itemId}: ${row.reason}`}
                  >
                    <span className="mono">{row.itemId}</span>
                    <span className="exception-reason" title={row.reason}>
                      {row.reason}
                    </span>
                    <span className="exception-site" title={row.siteName}>
                      {row.siteName}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="dashboard-section-divider" aria-hidden="true" />

        <section className="panel dashboard-status-panel">
          <h2>Status distribution</h2>
          <div className="ranked-bars">
            {statusBreakdown.map((row) => (
              <Link
                to={withPartType(`/inventory?status=${row.status}`, partType)}
                className="ranked-bar"
                key={row.status}
              >
                <span className="ranked-bar-heading">
                  <span className="ranked-bar-label">{row.label}</span>
                  <span className="ranked-bar-value mono">{row.count.toLocaleString()}</span>
                </span>
                <span className="ranked-bar-track" aria-hidden="true">
                  <span
                    className="ranked-bar-fill"
                    style={{
                      width: `${(row.count / largestStatusCount) * 100}%`,
                      background: `var(${row.cssVar})`,
                    }}
                  />
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="panel lifecycle-panel" aria-label="Fleet by lifecycle family">
        <div className="panel-heading">
          <h2>Lifecycle overview</h2>
          <div className="lifecycle-heading-tools">
            <span className="panel-context">{fleetTotal.toLocaleString()} items</span>
            <details className="lifecycle-definitions">
              <summary>Status definitions</summary>
              <dl className="lifecycle-definitions-popover">
                {familySegments.map((segment) => (
                  <div key={segment.family}>
                    <dt>{segment.label}</dt>
                    <dd>
                      {statusesForFamily(segment.family)
                        .map((status) => statusLabels[status])
                        .join(', ')}
                    </dd>
                  </div>
                ))}
              </dl>
            </details>
          </div>
        </div>
        <div
          className="family-bar"
          role="img"
          aria-label="Distribution of inventory across lifecycle families"
        >
          {familySegments.map((segment) => {
            const percent = (segment.count / fleetTotal) * 100;
            const widthPercent = segment.count > 0 ? Math.max(percent, 1) : 0;
            const includedStatuses = statusesForFamily(segment.family)
              .map((status) => statusLabels[status])
              .join(', ');
            if (widthPercent === 0) return null;
            return (
              <Link
                key={segment.family}
                to={withPartType(`/inventory?family=${segment.family}`, partType)}
                className="family-bar-segment"
                style={{ width: `${widthPercent}%`, background: `var(${segment.cssVar})` }}
                title={`${segment.label}: ${segment.count.toLocaleString()} · ${includedStatuses}`}
                aria-label={`${segment.label}: ${segment.count.toLocaleString()}. Includes ${includedStatuses}`}
              />
            );
          })}
        </div>
        <ul className="family-legend">
          {familySegments.map((segment) => (
            <li key={segment.family}>
              <Link to={withPartType(`/inventory?family=${segment.family}`, partType)}>
                <span
                  className="family-swatch"
                  style={{ background: `var(${segment.cssVar})` }}
                  aria-hidden="true"
                />
                {segment.label}
                <span className="mono family-legend-count">{segment.count.toLocaleString()}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel operations-summary">
        <div
          className={
            summaryView === 'allocation'
              ? 'summary-heading has-allocation-filter'
              : 'summary-heading'
          }
        >
          <h2>Inventory summaries</h2>
          <span className="summary-scope">{summaryScope}</span>
          {summaryView === 'allocation' ? (
            <label className="summary-allocation-filter">
              <span>Customer</span>
              <SearchableSelect
                ariaLabel="Allocation customer"
                onChange={updateAllocationCustomer}
                options={[
                  {
                    value: '',
                    label: 'All customers',
                    count: customerAllocation.rows.length,
                    countLabel: 'customers',
                  },
                  ...customerAllocation.rows.map((row) => ({
                    value: row.customerId,
                    label: row.customerName,
                    count: row.total,
                    countLabel: allocationView === 'cameras' ? 'cameras' : 'items',
                  })),
                ]}
                placeholder="All customers"
                value={allocationCustomerId}
              />
            </label>
          ) : null}
          <div className="summary-tabs" role="tablist" aria-label="Inventory summary view">
            <button
              type="button"
              role="tab"
              aria-selected={summaryView === 'system'}
              className={summaryView === 'system' ? 'active' : ''}
              onClick={() => setSummaryView('system')}
            >
              System summary
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={summaryView === 'allocation'}
              className={summaryView === 'allocation' ? 'active' : ''}
              onClick={() => setSummaryView('allocation')}
            >
              Customer allocation
            </button>
          </div>
        </div>

        {summaryView === 'allocation' ? (
          <>
            <div className="allocation-toolbar">
              <div className="allocation-view-toggle" role="group" aria-label="Allocation columns">
                <button
                  type="button"
                  aria-pressed={allocationView === 'devices'}
                  className={allocationView === 'devices' ? 'active' : ''}
                  onClick={() => updateAllocationView('devices')}
                >
                  Devices
                </button>
                <button
                  type="button"
                  aria-pressed={allocationView === 'cameras'}
                  className={allocationView === 'cameras' ? 'active' : ''}
                  onClick={() => updateAllocationView('cameras')}
                >
                  Cameras
                </button>
              </div>
              {allocationView === 'cameras' ? (
                <>
                  <label className="allocation-month">
                    <span>Pens in</span>
                    <select
                      aria-label="Month for active pens"
                      value={allocationMonth}
                      onChange={(event) => updateAllocationMonth(event.target.value)}
                    >
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {monthLabel(month)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="allocation-toolbar-note" role="status">
                    {penActivityNote} · other columns as of today · all generations combined ·
                    Tidal's own cameras left out · hover or tap a column for its definition
                  </span>
                </>
              ) : null}
              <button
                type="button"
                className="allocation-export"
                onClick={exportAllocationCsv}
                disabled={
                  displayedCustomerAllocation.length === 0 ||
                  (allocationView === 'cameras' && penActivity.isPending)
                }
              >
                Export CSV
              </button>
            </div>
            <div
              className={
                allocationView === 'cameras'
                  ? 'summary-table camera-allocation-table'
                  : 'summary-table'
              }
            >
              <table>
                <thead>
                  <tr>
                    <th scope="col">Customer</th>
                    {allocationColumns.map((column) =>
                      allocationView === 'cameras' ? (
                        <th scope="col" key={column.key}>
                          <HeaderHint label={column.label} definition={column.title} />
                        </th>
                      ) : (
                        <th scope="col" key={column.key} title={column.title}>
                          {column.label}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayedCustomerAllocation.map((row) => {
                    const customerKey = `customer:${row.customerId}`;
                    const expanded = expandedAllocationRows.has(customerKey);
                    return (
                      <Fragment key={row.customerId}>
                        <tr key={row.customerId} className={expanded ? 'is-expanded' : undefined}>
                          <th scope="row">
                            <button
                              type="button"
                              className="allocation-row-toggle"
                              aria-expanded={expanded}
                              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.customerName}`}
                              onClick={() => toggleAllocationRow(customerKey)}
                            >
                              {expanded ? '▾' : '▸'}
                            </button>
                            <Link
                              to={withPartType(
                                `/inventory?customerId=${encodeURIComponent(row.customerId)}`,
                                partType,
                              )}
                            >
                              {row.customerName}
                            </Link>
                          </th>
                          {allocationColumns.map((column) => (
                            <AllocationCell key={column.key} column={column} counts={row.counts} />
                          ))}
                        </tr>
                        {expanded
                          ? row.countries.map((country) => {
                              const countryKey = `${customerKey}:country:${country.key}`;
                              const countryExpanded = expandedAllocationRows.has(countryKey);
                              return (
                                <Fragment key={countryKey}>
                                  <tr className="allocation-country-row">
                                    <th scope="row">
                                      <button
                                        type="button"
                                        className="allocation-row-toggle"
                                        aria-expanded={countryExpanded}
                                        aria-label={`${countryExpanded ? 'Collapse' : 'Expand'} ${country.label} for ${row.customerName}`}
                                        onClick={() => toggleAllocationRow(countryKey)}
                                      >
                                        {countryExpanded ? '▾' : '▸'}
                                      </button>
                                      <Link
                                        to={allocationSiteHref(row.customerId, country.siteIds)}
                                      >
                                        {country.label}
                                      </Link>
                                    </th>
                                    {allocationColumns.map((column) => (
                                      <AllocationCell
                                        key={column.key}
                                        column={column}
                                        counts={country.counts}
                                      />
                                    ))}
                                  </tr>
                                  {countryExpanded
                                    ? country.regions.map((region) => {
                                        const regionKey = `${countryKey}:region:${region.key}`;
                                        const regionExpanded =
                                          expandedAllocationRows.has(regionKey);
                                        return (
                                          <Fragment key={regionKey}>
                                            <tr className="allocation-region-row">
                                              <th scope="row">
                                                <button
                                                  type="button"
                                                  className="allocation-row-toggle"
                                                  aria-expanded={regionExpanded}
                                                  aria-label={`${regionExpanded ? 'Collapse' : 'Expand'} ${region.label} in ${country.label}`}
                                                  onClick={() => toggleAllocationRow(regionKey)}
                                                >
                                                  {regionExpanded ? '▾' : '▸'}
                                                </button>
                                                <Link
                                                  to={allocationSiteHref(
                                                    row.customerId,
                                                    region.siteIds,
                                                  )}
                                                >
                                                  {region.label}
                                                </Link>
                                              </th>
                                              {allocationColumns.map((column) => (
                                                <AllocationCell
                                                  key={column.key}
                                                  column={column}
                                                  counts={region.counts}
                                                />
                                              ))}
                                            </tr>
                                            {regionExpanded
                                              ? region.sites.map((site) => (
                                                  <tr
                                                    className="allocation-site-row"
                                                    key={`${regionKey}:site:${site.siteId}`}
                                                  >
                                                    <th scope="row">
                                                      <Link
                                                        to={withPartType(
                                                          `/inventory?assignedSiteId=${encodeURIComponent(site.siteId)}`,
                                                          partType,
                                                        )}
                                                      >
                                                        {site.label}
                                                      </Link>
                                                    </th>
                                                    {allocationColumns.map((column) => (
                                                      <AllocationCell
                                                        key={column.key}
                                                        column={column}
                                                        counts={site.counts}
                                                      />
                                                    ))}
                                                  </tr>
                                                ))
                                              : null}
                                          </Fragment>
                                        );
                                      })
                                    : null}
                                </Fragment>
                              );
                            })
                          : null}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total</th>
                    {allocationColumns.map((column) => (
                      <AllocationCell
                        key={column.key}
                        column={column}
                        counts={displayedAllocationTotals}
                        showZero
                      />
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="summary-table system-summary-table">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Location</th>
                    {deviceCategories.map((category) => (
                      <th scope="col" key={category.key} title={category.label}>
                        {category.shortLabel}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {systemSummary.groups.map((group) => (
                    <tr key={group.key}>
                      <th scope="row">{group.label}</th>
                      {deviceCategories.map((category) => (
                        <td key={category.key}>
                          {group.totals[category.key]
                            ? group.totals[category.key].toLocaleString()
                            : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total</th>
                    {deviceCategories.map((category) => (
                      <td key={category.key}>
                        {systemSummary.totals[category.key].toLocaleString()}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            <details className="system-status-detail">
              <summary>Status breakdown</summary>
              <div className="system-status-grid">
                {systemSummary.groups.map((group) => (
                  <section key={group.key} className="status-detail-group">
                    <h3>{group.label}</h3>
                    <div className="summary-table status-detail-table">
                      <table>
                        <thead>
                          <tr>
                            <th scope="col">Status</th>
                            {deviceCategories.map((category) => (
                              <th scope="col" key={category.key} title={category.label}>
                                {category.shortLabel}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => (
                            <tr key={row.status}>
                              <th scope="row">{row.statusLabel}</th>
                              {deviceCategories.map((category) => (
                                <td key={category.key}>
                                  {row.counts[category.key]
                                    ? row.counts[category.key].toLocaleString()
                                    : '—'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>
            </details>
          </>
        )}
      </section>

      {revisionBreakdowns.map((breakdown) => (
        <section
          key={breakdown.category}
          className="panel revision-breakdown"
          aria-labelledby={`revisions-${breakdown.category}`}
        >
          <div className="panel-heading">
            <h2 id={`revisions-${breakdown.category}`}>{breakdown.label} versions</h2>
            <span className="panel-context">
              {breakdown.total.toLocaleString()} items
              {breakdown.unversioned
                ? ` · ${breakdown.unversioned.toLocaleString()} without a version`
                : ''}
            </span>
          </div>
          <div className="summary-table">
            <table>
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  {breakdown.revisions.map((revision) => (
                    <th scope="col" key={revision}>
                      {revision}
                    </th>
                  ))}
                  <th scope="col">No version</th>
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.rows.map((row) => (
                  <tr key={row.status}>
                    <th scope="row">{row.statusLabel}</th>
                    {row.counts.map((count, index) => (
                      <td key={breakdown.revisions[index] ?? 'none'}>
                        {count ? count.toLocaleString() : '—'}
                      </td>
                    ))}
                    <td>{row.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {breakdown.totals.map((count, index) => (
                    <td key={breakdown.revisions[index] ?? 'none'}>{count.toLocaleString()}</td>
                  ))}
                  <td>{breakdown.total.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      ))}

      <div className="dashboard-detail-grid">
        <section className="panel">
          <h2>Assigned vs. physical location</h2>
          <table className="assigned-location-table">
            <thead>
              <tr>
                <th scope="col">Site</th>
                <th scope="col">Assigned</th>
                <th scope="col">Located</th>
              </tr>
            </thead>
            <tbody>
              {assignedVsLocation.map((row) => (
                <tr key={row.siteId}>
                  <td>
                    {row.siteName}
                    {row.customerName ? (
                      <span className="dimmed-inline"> · {row.customerName}</span>
                    ) : null}
                  </td>
                  <td className="mono">{row.assignedCount.toLocaleString()}</td>
                  <td className="mono">{row.locatedCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <div className="dashboard-section-divider" aria-hidden="true" />

        <section className="panel">
          <h2>Inventory by part type</h2>
          <div className="part-type-grid">
            {partTypeBreakdown.map((row) => {
              const generationSegments = generationSegmentsByPartType[row.type];
              const generationLabel = generationSegments
                ?.map((segment) => `${segment.label} ${generationDeviceCounts[segment.key]}`)
                .join(', ');

              return (
                <Link
                  key={row.type}
                  to={`/inventory?partType=${encodeURIComponent(row.type)}`}
                  className="part-type-cell"
                  aria-label={`${row.label}: ${row.count.toLocaleString()} serialized items${
                    generationLabel ? `. Eligible build split: ${generationLabel}` : ''
                  }`}
                >
                  <div className="part-type-label">
                    <span>{row.label}</span>
                    <span className="mono">{row.count.toLocaleString()}</span>
                    {generationSegments ? (
                      <span className="part-type-generation-summary" aria-hidden="true">
                        {generationSegments.map((segment) => (
                          <span key={segment.key} style={{ color: `var(${segment.cssVar})` }}>
                            {segment.label} {generationDeviceCounts[segment.key]}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </div>
                  {generationSegments ? (
                    <div className="part-type-track" aria-hidden="true">
                      <div
                        className="part-type-generation-fills"
                        style={{ width: `${(row.count / largestPartCount) * 100}%` }}
                      >
                        {generationSegments.map((segment) => (
                          <span
                            className="part-type-generation-segment"
                            key={segment.key}
                            style={{
                              width: `${
                                (generationDeviceCounts[segment.key] / Math.max(row.count, 1)) * 100
                              }%`,
                              background: `var(${segment.cssVar})`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="part-type-track">
                      <div
                        className="part-type-fill"
                        style={{ width: `${(row.count / largestPartCount) * 100}%` }}
                      />
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </>
  );
}
